import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import TransparentButton from "@/components/TransparentButton/TransparentButton";
import TransparentTile from "@/components/TransparentTile/TransparentTile";
import { formatGrosze, tipFromPercentGrosze } from "@/helpers/cartPricing";
import {
  useCartActions,
  useCartSubtotal,
  useCartTipChosen,
  useCartTipGrosze,
} from "@/store/useCartStore";
import {
  DEFAULT_TIP_PERCENT,
  TIP_PERCENTAGES,
} from "@/store/useTipDraftStore";
import styles from "./OrderTip.module.scss";

/**
 * Tip picker for the ordering flow.
 *
 * Deliberately not the existing LeaveTip drawer: that one reads
 * selectedAmount/tipAmount off the shared store in major units (złoty floats),
 * while the cart works in grosze end to end. Reusing it would mean converting
 * back and forth around every calculation.
 */
function OrderTip() {
  const { t } = useTranslation();
  const subtotal = useCartSubtotal();
  const tipGrosze = useCartTipGrosze();
  const { setTipGrosze } = useCartActions();
  const tipChosen = useCartTipChosen();

  // Opens on the default rather than on nothing. Once the guest has answered,
  // 0% included, their answer stands.
  useEffect(() => {
    if (!tipChosen) {
      setTipGrosze(tipFromPercentGrosze(subtotal, DEFAULT_TIP_PERCENT));
    }
  }, [tipChosen, subtotal, setTipGrosze]);

  const selectedPercent =
    TIP_PERCENTAGES.find(
      (percent) => tipFromPercentGrosze(subtotal, percent) === tipGrosze,
    ) ?? null;

  return (
    <div className={styles.orderTipContent}>
      <p className={`body-medium ${styles.orderTipHeader}`}>
        {t("order.tip.header")}
      </p>

      <TransparentTile>
        <>
          <p className={`body-medium ${styles.orderTipAmountLabel}`}>
            {t("order.tip.amount")}
          </p>
          <p className={`headline-medium ${styles.orderTipAmountValue}`}>
            {formatGrosze(tipGrosze)}
          </p>
        </>
      </TransparentTile>

      <div className={styles.orderTipOptions}>
        {TIP_PERCENTAGES.map((percent) => (
          <TransparentButton
            key={percent}
            text={`${percent}%`}
            value={`${percent}`}
            // Not a truthiness check: 0% is a real choice and would fail one.
            selectedValue={
              selectedPercent === null ? "" : `${selectedPercent}`
            }
            setValue={() =>
              setTipGrosze(tipFromPercentGrosze(subtotal, percent))
            }
          />
        ))}
      </div>
    </div>
  );
}

export default OrderTip;
